import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/button'
import { Spinner } from '@/components/common'
import { ArrowRightLine } from '@/components/icons'
import { useGrantConsent } from '@/services'
import { ExternalScreen } from './ExternalScreen'
import {
  buildGrants,
  buildInitialToggles,
  buildPermissionsPayload,
  isRequiredPermission,
  permissionLabel,
  resolveExistingGrants,
  visiblePermissions,
} from './consentPermissions'
import { ConsentChallengeResponse, RichRequiredAction, User } from '@/types'

interface ConsentScreenProps {
  challenge: ConsentChallengeResponse
  onComplete: (accessToken: string, refreshToken: string, user: User, requirements?: RichRequiredAction[]) => void
  /** `error` is the raw request failure, so the caller can read a 428's `requirements`. */
  onError: (message: string, error?: unknown) => void
  onCancel: () => void
}

export const ConsentScreen: React.FC<ConsentScreenProps> = ({ challenge, onComplete, onError, onCancel }) => {
  const { i18n } = useTranslation()
  // Every descriptor the server sent, in its order — required and optional interleaved as it chose.
  const permissions = visiblePermissions(challenge.permissions)

  const [toggles, setToggles] = useState<Record<string, boolean>>(() =>
    buildInitialToggles(
      challenge.permissions,
      resolveExistingGrants(challenge.existingGrants, challenge.existingPermissions)
    )
  )

  const { mutate: grantConsent, isLoading } = useGrantConsent()

  const handleAuthorize = () => {
    const grants = buildGrants(challenge.permissions, toggles)
    grantConsent(
      {
        consentToken: challenge.consentToken,
        // `grants` is what a current server reads; `permissions` keeps an older one working.
        grants,
        permissions: buildPermissionsPayload(grants),
        useExistingConsent: false,
      },
      {
        onSuccess: (data) => {
          onComplete(data.result.accessToken, data.result.refreshToken, data.result.user, data.result.requirements)
        },
        onError: (error: any) => {
          const message = error?.response?.data?.message || 'Consent session expired. Please log in again.'
          onError(message, error)
        },
      }
    )
  }

  const handleDeny = () => {
    window.parent.postMessage({ error: 'consent_denied' }, '*')
    onCancel()
  }

  const togglePermission = (key: string) => {
    setToggles((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <ExternalScreen>
      <div className="rounded-2xl bg-white px-6 py-6">
        {/* Header */}
        <p className="text-center text-sm font-medium text-gray-500 mb-4">
          An external platform is requesting access to your DUPR data
        </p>

        {/* DUPR → Partner flow indicator */}
        <div className="flex items-center justify-center gap-x-4 py-4 my-3 border-y border-gray-100">
          <img src="/img/dupr_dark.svg" alt="DUPR" className="h-6" />
          <ArrowRightLine className="w-5 h-5 text-gray-400" />
          <span className="text-base font-bold text-[#0b223f]">{challenge.partnerName}</span>
        </div>

        {/* Explanatory text */}
        <p className="text-xs text-gray-500 text-center leading-relaxed mb-5">
          By authorizing, <span className="font-medium text-gray-600">{challenge.partnerName}</span> will have access to
          the following data. You may modify or revoke this authorization at any time from your profile settings.
        </p>

        {/* Permission rows — one per server descriptor, in the server's order. Required rows are
            locked on; optional ones toggle. */}
        <div className="flex flex-col gap-y-3">
          {permissions.map((perm) => {
            const label = permissionLabel(perm, i18n)
            if (isRequiredPermission(perm)) {
              return (
                <div key={perm.key} className="flex items-center px-3 py-2 rounded-lg bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <span className="block text-sm text-gray-400">{label}</span>
                    {perm.description && <span className="block text-xs text-gray-400">{perm.description}</span>}
                  </div>
                  <span className="text-[10px] font-medium text-gray-400 uppercase mx-3">Required</span>
                  <div className="w-9 h-5 shrink-0 rounded-full bg-gray-500 relative cursor-default">
                    <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-white shadow" />
                  </div>
                </div>
              )
            }
            return (
              <div
                key={perm.key}
                className="flex items-center px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => togglePermission(perm.key)}
              >
                <div className="flex-1 min-w-0">
                  <span className="block text-sm text-[#0b223f]">{label}</span>
                  {perm.description && <span className="block text-xs text-gray-500">{perm.description}</span>}
                </div>
                <span className="text-[10px] font-medium text-gray-400 uppercase mx-3">Optional</span>
                <div
                  className={`w-9 h-5 shrink-0 rounded-full relative transition-colors ${
                    toggles[perm.key] ? 'bg-primary-500' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${
                      toggles[perm.key] ? 'right-0.5' : 'left-0.5'
                    }`}
                  />
                </div>
              </div>
            )
          })}
        </div>

        {/* Buttons */}
        <div className="flex flex-col items-center gap-y-2 mt-6">
          <Button
            disabled={isLoading}
            className="text-white text-sm font-semibold !bg-primary-500 disabled:opacity-50 w-full h-11 flex flex-col items-center justify-center rounded-full"
            onClick={handleAuthorize}
          >
            <div className="flex items-center gap-x-2">
              <span>{isLoading ? 'Authorizing...' : 'Authorize'}</span>
              {isLoading && <Spinner className="w-4 h-4 fill-primary-500" />}
            </div>
          </Button>
          <button
            disabled={isLoading}
            className="text-sm font-semibold text-red-500 hover:bg-red-50 transition-colors w-full h-11 rounded-full border border-red-300"
            onClick={handleDeny}
          >
            Deny access
          </button>
        </div>
      </div>
    </ExternalScreen>
  )
}
